/**
 * decide(state, command, ctx) checks a command against the current state and returns the
 * events it produces, or an error. It is pure: no clock, no network, no randomness. The
 * same inputs always give the same output, which is what makes it safe to run on the phone
 * (for an instant screen update) and again on the server (to make it permanent).
 *
 * A command that changes nothing returns ok([]): no events, nothing stored.
 */
import type { Command } from "./commands.ts";
import type { EventBody, NewEvent, StoredEvent } from "./events.ts";
import { err, ok, type Result } from "./result.ts";
import { splitByFromText } from "./engine/split.ts";
import {
  receiptKey,
  tripKey,
  type GroupState,
  type Item,
  type ItemFields,
  type Payment,
  type Person,
  type Settings,
} from "./types.ts";

export interface DecideContext {
  /** Looks up a stored event by id (only undo needs this). */
  findEvent(eventId: string): StoredEvent | undefined;
}

export const noHistory: DecideContext = { findEvent: () => undefined };

export function decide(
  state: GroupState,
  cmd: Command,
  ctx: DecideContext = noHistory,
): Result<NewEvent[]> {
  switch (cmd.type) {
    case "group.create":
      if (state.name !== "") return err("already_exists", "This group already exists");
      return ok([{ type: "group.created", data: { name: cmd.name } }]);

    case "settings.update": {
      const p = cmd.patch;
      if (p.ownerPersonId && !state.people[p.ownerPersonId]) {
        return err("not_found", "That person isn't in this group");
      }
      const { before, after } = changed<Settings>(state.settings, p);
      return ok(after ? [{ type: "settings.updated", data: { before, after } }] : []);
    }

    case "store.set": {
      const current = state.stores[cmd.name] ?? null;
      const next = {
        name: cmd.name,
        taxBp: cmd.taxBp,
        category: cmd.category,
        ...(cmd.tipBp ? { tipBp: cmd.tipBp } : {}),
        ...(cmd.mcc ? { mcc: cmd.mcc } : {}),
      };
      if (
        current &&
        current.taxBp === next.taxBp &&
        (current.tipBp ?? 0) === (next.tipBp ?? 0) &&
        (current.mcc ?? "") === (next.mcc ?? "") &&
        current.category === next.category
      ) {
        return ok([]);
      }
      return ok([{ type: "store.set", data: { name: cmd.name, before: current, after: next } }]);
    }

    case "store.remove": {
      const current = state.stores[cmd.name];
      if (!current) return err("not_found", `There's no store preset named ${cmd.name}`);
      return ok([{ type: "store.set", data: { name: cmd.name, before: current, after: null } }]);
    }

    case "person.add": {
      if (state.people[cmd.personId]) return err("already_exists", "That person already exists");
      const clash = findByName(state, cmd.name);
      if (clash) return duplicateName(cmd.name);
      const person: Person = {
        id: cmd.personId,
        name: cmd.name.trim(),
        color: cmd.color,
        archived: false,
      };
      return ok([{ type: "person.added", data: { person } }]);
    }

    case "person.update": {
      const person = state.people[cmd.personId];
      if (!person) return err("not_found", "That person isn't in this group");
      if (cmd.patch.name !== undefined) {
        const clash = findByName(state, cmd.patch.name);
        if (clash && clash.id !== person.id) return duplicateName(cmd.patch.name);
      }
      const { before, after } = changed(person, cmd.patch);
      return ok(
        after ? [{ type: "person.updated", data: { personId: person.id, before, after } }] : [],
      );
    }

    case "item.add": {
      if (state.items[cmd.itemId]) return err("already_exists", "That item already exists");
      const check = checkItemRefs(state, cmd.fields, cmd.ticked);
      if (!check.ok) return check;
      const ticked = [...new Set(cmd.ticked)];
      // Its receipt: the one given, else the one already at this store, date and label, else a
      // new receipt named after this item (decide has no randomness), unless older items without
      // ids are there (they're grouped by store, date and label until they get one).
      const receiptId =
        cmd.fields.receiptId ??
        receiptIdAt(state, cmd.fields, null) ??
        (anyAt(state, cmd.fields) ? null : `i-${cmd.itemId}`);
      const item: Item = {
        id: cmd.itemId,
        ...cmd.fields,
        ...(receiptId ? { receiptId } : {}),
        splitBy: splitByFromText(cmd.fields.customSplit, everyoneIn(state)),
        ticked,
      };
      return ok([{ type: "item.added", data: { item, position: null } }]);
    }

    case "item.update": {
      const item = state.items[cmd.itemId];
      if (!item) return err("not_found", "That item no longer exists");
      const check = checkItemRefs(state, cmd.patch, []);
      if (!check.ok) return check;
      const patch: Partial<ItemFields> = { ...cmd.patch };
      // The split is kept by person id, read from the text when the text is saved.
      if (patch.customSplit !== undefined)
        patch.splitBy = splitByFromText(patch.customSplit, everyoneIn(state));
      // Moved onto a receipt that has an id (another receipt of the visit, say): it joins that
      // receipt. Otherwise it keeps its own, so renaming a receipt's store or date keeps it whole.
      if (
        patch.receiptId === undefined &&
        (["date", "store", "receipt"] as const).some(
          (k) => patch[k] !== undefined && (patch[k] ?? "") !== (item[k] ?? ""),
        )
      ) {
        const target = receiptIdAt(state, { ...item, ...patch }, item.id);
        if (target && target !== item.receiptId) patch.receiptId = target;
      }
      const { before, after } = changed<ItemFields>(item, patch);
      return ok(after ? [{ type: "item.updated", data: { itemId: item.id, before, after } }] : []);
    }

    case "item.remove": {
      const item = state.items[cmd.itemId];
      if (!item) return err("not_found", "That item no longer exists");
      const position = state.itemOrder.indexOf(item.id);
      return ok([{ type: "item.removed", data: { item, position } }]);
    }

    case "share.set": {
      const item = state.items[cmd.itemId];
      if (!item) return err("not_found", "That item no longer exists");
      if (!state.people[cmd.personId]) return err("not_found", "That person isn't in this group");
      const before = item.ticked.includes(cmd.personId);
      if (before === cmd.ticked) return ok([]);
      return ok([
        {
          type: "share.set",
          data: { itemId: item.id, personId: cmd.personId, ticked: cmd.ticked, before },
        },
      ]);
    }

    case "payment.add": {
      if (state.payments[cmd.paymentId]) {
        return err("already_exists", "That payment already exists");
      }
      if (!state.people[cmd.personId]) return err("not_found", "That person isn't in this group");
      const payment: Payment = {
        id: cmd.paymentId,
        at: cmd.at,
        personId: cmd.personId,
        cents: cmd.cents,
        note: cmd.note,
      };
      return ok([{ type: "payment.added", data: { payment } }]);
    }

    case "payment.update": {
      const payment = state.payments[cmd.paymentId];
      if (!payment) return err("not_found", "That payment no longer exists");
      if (cmd.patch.personId && !state.people[cmd.patch.personId]) {
        return err("not_found", "That person isn't in this group");
      }
      const { before, after } = changed(payment, cmd.patch);
      return ok(
        after ? [{ type: "payment.updated", data: { paymentId: payment.id, before, after } }] : [],
      );
    }

    case "payment.remove": {
      const payment = state.payments[cmd.paymentId];
      if (!payment) return err("not_found", "That payment no longer exists");
      return ok([{ type: "payment.removed", data: { payment } }]);
    }

    case "receipt.set": {
      const receipt = cmd.receipt ?? "";
      // Said by store, date and label (the AI, an import, an older screen): the receipt there.
      const receiptId = cmd.receiptId ?? receiptIdAt(state, { ...cmd, receipt }, null) ?? undefined;
      const before =
        state.receipts[receiptKey({ ...cmd, receipt, receiptId })] ??
        (receiptId ? state.receipts[tripKey(cmd.date, cmd.store, receipt)] : undefined) ??
        null;
      if (before === cmd.cents) return ok([]);
      return ok([
        {
          type: "receipt.set",
          data: {
            date: cmd.date,
            store: cmd.store,
            ...(receipt ? { receipt } : {}),
            ...(receiptId ? { receiptId } : {}),
            before,
            after: cmd.cents,
          },
        },
      ]);
    }

    case "catalog.note": {
      const before = state.catalogNotes?.[cmd.key] ?? null;
      const after = cmd.note ? { ...cmd.note, keywords: [...new Set(cmd.note.keywords)] } : null;
      if (JSON.stringify(before) === JSON.stringify(after)) return ok([]);
      return ok([{ type: "catalog.noted", data: { key: cmd.key, before, after } }]);
    }

    case "undo":
      return undo(state, cmd.eventId, ctx);

    case "import.mark":
      return ok([
        { type: "import.marked", data: { source: cmd.source, capturedAt: cmd.capturedAt } },
      ]);

    default: {
      const unhandled: never = cmd;
      return unhandled;
    }
  }
}

/**
 * Undo builds the events that reverse an earlier event, checked against the state as it is
 * now. Nothing is deleted from the log; the reversal is recorded as new events.
 */
function undo(state: GroupState, eventId: string, ctx: DecideContext): Result<NewEvent[]> {
  const target = ctx.findEvent(eventId);
  if (!target) return err("not_found", "That change isn't in the history");
  const reversed = reverse(state, target);
  if (!reversed.ok) return reversed;
  return ok(reversed.value.map((e) => ({ ...e, meta: { undoOf: target.id } })));
}

function reverse(state: GroupState, e: StoredEvent): Result<EventBody[]> {
  switch (e.type) {
    case "item.added": {
      const item = state.items[e.data.item.id];
      if (!item) return err("cannot_undo", "That item was already removed");
      return ok([
        { type: "item.removed", data: { item, position: state.itemOrder.indexOf(item.id) } },
      ]);
    }
    case "item.removed": {
      if (state.items[e.data.item.id]) return err("cannot_undo", "That item is already back");
      const item = { ...e.data.item, ticked: e.data.item.ticked.filter((p) => state.people[p]) };
      return ok([{ type: "item.added", data: { item, position: e.data.position } }]);
    }
    case "item.updated": {
      const item = state.items[e.data.itemId];
      if (!item) return err("cannot_undo", "That item no longer exists");
      const { before, after } = changed<ItemFields>(item, e.data.before);
      return ok(after ? [{ type: "item.updated", data: { itemId: item.id, before, after } }] : []);
    }
    case "share.set": {
      const item = state.items[e.data.itemId];
      if (!item) return err("cannot_undo", "That item no longer exists");
      if (!state.people[e.data.personId]) return err("cannot_undo", "That person was removed");
      const now = item.ticked.includes(e.data.personId);
      if (now === e.data.before) return ok([]);
      return ok([
        {
          type: "share.set",
          data: { itemId: item.id, personId: e.data.personId, ticked: e.data.before, before: now },
        },
      ]);
    }
    case "payment.added": {
      const payment = state.payments[e.data.payment.id];
      if (!payment) return err("cannot_undo", "That payment was already removed");
      return ok([{ type: "payment.removed", data: { payment } }]);
    }
    case "payment.removed": {
      if (state.payments[e.data.payment.id]) {
        return err("cannot_undo", "That payment is already back");
      }
      if (!state.people[e.data.payment.personId]) {
        return err("cannot_undo", "That person was removed");
      }
      return ok([{ type: "payment.added", data: { payment: e.data.payment } }]);
    }
    case "payment.updated": {
      const payment = state.payments[e.data.paymentId];
      if (!payment) return err("cannot_undo", "That payment no longer exists");
      const { before, after } = changed(payment, e.data.before);
      return ok(
        after ? [{ type: "payment.updated", data: { paymentId: payment.id, before, after } }] : [],
      );
    }
    case "person.added": {
      const person = state.people[e.data.person.id];
      if (!person) return err("cannot_undo", "That person was already removed");
      if (personInUse(state, person.id)) {
        return err("in_use", `${person.name} has items or payments; archive them instead`);
      }
      return ok([{ type: "person.removed", data: { person } }]);
    }
    case "person.removed": {
      if (state.people[e.data.person.id]) return err("cannot_undo", "That person is already back");
      if (findByName(state, e.data.person.name)) return duplicateName(e.data.person.name);
      return ok([{ type: "person.added", data: { person: e.data.person } }]);
    }
    case "person.updated": {
      const person = state.people[e.data.personId];
      if (!person) return err("cannot_undo", "That person was removed");
      if (e.data.before.name !== undefined) {
        const clash = findByName(state, e.data.before.name);
        if (clash && clash.id !== person.id) return duplicateName(e.data.before.name);
      }
      const { before, after } = changed(person, e.data.before);
      return ok(
        after ? [{ type: "person.updated", data: { personId: person.id, before, after } }] : [],
      );
    }
    case "store.set": {
      const current = state.stores[e.data.name] ?? null;
      return ok([
        { type: "store.set", data: { name: e.data.name, before: current, after: e.data.before } },
      ]);
    }
    case "receipt.set": {
      const current = state.receipts[receiptKey(e.data)] ?? null;
      return ok([
        {
          type: "receipt.set",
          data: {
            date: e.data.date,
            store: e.data.store,
            ...(e.data.receipt ? { receipt: e.data.receipt } : {}),
            ...(e.data.receiptId ? { receiptId: e.data.receiptId } : {}),
            before: current,
            after: e.data.before,
          },
        },
      ]);
    }
    case "settings.updated": {
      // A setting that was absent before (saved as nothing) goes back to what absent means.
      const back: Partial<Settings> = { ...e.data.before };
      for (const k of Object.keys(e.data.after) as (keyof Settings)[])
        if (back[k] === undefined && k in ABSENT_SETTINGS)
          (back as Record<string, unknown>)[k] = ABSENT_SETTINGS[k as keyof typeof ABSENT_SETTINGS];
      const { before, after } = changed<Settings>(state.settings, back);
      return ok(after ? [{ type: "settings.updated", data: { before, after } }] : []);
    }
    case "catalog.noted": {
      const current = state.catalogNotes?.[e.data.key] ?? null;
      return ok([
        { type: "catalog.noted", data: { key: e.data.key, before: current, after: e.data.before } },
      ]);
    }
    case "group.created":
    case "import.marked":
      return err("cannot_undo", "This entry can't be undone");
    default: {
      const unhandled: never = e;
      return unhandled;
    }
  }
}

// ---------------------------------------------------------------- helpers

/**
 * Compares `patch` with `current` and keeps only the keys whose values differ.
 * Returns after = null when nothing changed.
 */
/** What an absent setting means, so undoing its first change restores exactly that. */
const ABSENT_SETTINGS = {
  memberAccess: {},
  treasurerAccess: {},
  personAccess: {},
  receiptLocks: {},
  receiptsOpenByDefault: false,
  joining: "open",
} satisfies Partial<Settings>;

function changed<T extends object>(
  current: T,
  patch: Partial<T>,
): { before: Partial<T>; after: Partial<T> | null } {
  const before: Partial<T> = {};
  const after: Partial<T> = {};
  let any = false;
  for (const key of Object.keys(patch) as (keyof T)[]) {
    const next = patch[key];
    if (next === undefined) continue;
    if (JSON.stringify(current[key]) === JSON.stringify(next)) continue;
    before[key] = current[key];
    after[key] = next;
    any = true;
  }
  return { before, after: any ? after : null };
}

function norm(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}

function findByName(state: GroupState, name: string): Person | undefined {
  const n = norm(name);
  return Object.values(state.people).find((p) => norm(p.name) === n);
}

function duplicateName(name: string): Result<never> {
  return err(
    "duplicate_name",
    `Someone is already called ${name.trim()}. Use a different name, like "${name.trim()} L".`,
  );
}

/** Everyone in the group, in order (to read a Custom split's names into ids). */
function everyoneIn(state: GroupState): Person[] {
  return state.personOrder.map((id) => state.people[id]).filter((p): p is Person => !!p);
}

/**
 * The id of the receipt at this date, store and label, when the items already there have exactly
 * one (none, or several: null). `except` leaves out the item being edited.
 */
function receiptIdAt(
  state: GroupState,
  f: { date: string; store: string; receipt?: string },
  except: string | null,
): string | null {
  const key = tripKey(f.date, f.store, f.receipt ?? "");
  const ids = new Set<string>();
  for (const it of Object.values(state.items))
    if (it.id !== except && it.receiptId && tripKey(it.date, it.store, it.receipt ?? "") === key)
      ids.add(it.receiptId);
  return ids.size === 1 ? [...ids][0]! : null;
}

/** Whether any item is already at this date, store and label. */
function anyAt(state: GroupState, f: { date: string; store: string; receipt?: string }): boolean {
  const key = tripKey(f.date, f.store, f.receipt ?? "");
  return Object.values(state.items).some(
    (it) => tripKey(it.date, it.store, it.receipt ?? "") === key,
  );
}

function checkItemRefs(
  state: GroupState,
  fields: Partial<ItemFields>,
  ticked: readonly string[],
): Result<true> {
  for (const p of ticked) {
    if (!state.people[p]) return err("not_found", "A ticked person isn't in this group");
  }
  if (fields.paidBy && !state.people[fields.paidBy]) {
    return err("not_found", "The person in Paid by isn't in this group");
  }
  return ok(true);
}

function personInUse(state: GroupState, personId: string): boolean {
  return (
    Object.values(state.items).some((i) => i.ticked.includes(personId) || i.paidBy === personId) ||
    Object.values(state.payments).some((p) => p.personId === personId) ||
    state.settings.ownerPersonId === personId
  );
}
